import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Newspaper, Briefcase, ArrowRight, Share2, ChevronRight, Instagram, Facebook, Youtube, BookOpen, Video, Image as ImageIcon, Search } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { getEstimatedListingPrice, getGmpPercentage } from "@/lib/gmpUtils";
import { formatDisplayDate } from "@/lib/dateFormat";
import { sanitizeRichTextHtml } from "@/lib/htmlSanitizer";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { formatInr, formatIssueSizeCr, formatSignedInr } from "@/lib/numberFormat";
import { toWordPreview } from "@/lib/textPreview";
import "./my.css";
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

type GenericResult = {
  id: string;
  title: string;
  meta: string;
  link: string;
};

type IPOResult = GenericResult;

const ResultGroup = ({ title, items }: { title: string; items: GenericResult[] }) => (
  <div className="finance-card p-4">
    <h3 className="font-semibold text-foreground mb-3">{title}</h3>
    {items.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
    {items.length > 0 && (
      <div className="space-y-2">
        {items.map((item) => (
          <Link key={`${title}-${item.id}`} to={item.link} className="block rounded-lg px-3 py-2 hover:bg-muted/60 transition-colors">
            <p className="text-sm font-medium text-foreground line-clamp-1">{item.title}</p>
            <p className="text-xs text-muted-foreground line-clamp-1">{item.meta}</p>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const Index = () => {
  const { ipos, news, jobs, learning } = useData();
  const [globalSearchInput, setGlobalSearchInput] = useState("");
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  
  const latestIpos = ipos.slice(0, 3);
  const latestNews = news.slice(0, 3);
  const latestJobs = jobs.slice(0, 3);
  const latestLearning = learning.slice(0, 3);
  const normalizedGlobalSearch = globalSearchTerm.trim().toLowerCase();

  const globalResults = useMemo(() => {
    if (!normalizedGlobalSearch) {
      return {
        ipos: [] as IPOResult[],
        news: [] as GenericResult[],
        jobs: [] as GenericResult[],
        learning: [] as GenericResult[],
      };
    }

    const match = (...values: Array<string | number | undefined | null>) =>
      values
        .map((value) => String(value ?? ""))
        .join(" ")
        .toLowerCase()
        .includes(normalizedGlobalSearch);

    const ipoResults = ipos
      .filter((ipo) =>
        match(
          ipo.company,
          ipo.status,
          ipo.issueSize,
          ipo.priceBand,
          ipo.openDate,
          ipo.closeDate,
          ipo.listingDate,
          ipo.allotmentDate,
          formatDisplayDate(ipo.openDate),
          formatDisplayDate(ipo.closeDate)
        )
      )
      .slice(0, 6)
      .map((ipo) => ({
        id: ipo.id,
        title: ipo.company,
        meta: `${ipo.status.replace("_", " ").toUpperCase()} • Open ${formatDisplayDate(ipo.openDate)}`,
        link: `/ipos/${ipo.id}`,
      }));

    const newsResults = news
      .filter((item) =>
        match(item.title, item.summary, item.content, item.author, item.category, item.date, formatDisplayDate(item.date))
      )
      .slice(0, 6)
      .map((item) => ({
        id: item.id,
        title: item.title,
        meta: `${item.category} • ${formatDisplayDate(item.date)}`,
        link: `/news/${item.slug || item.id}`, // Use slug if available, fallback to ID
      }));

    const jobResults = jobs
      .filter((item) =>
        match(
          item.title,
          item.company,
          item.location,
          item.category,
          item.type,
          item.salary,
          item.description,
          item.postedDate,
          formatDisplayDate(item.postedDate)
        )
      )
      .slice(0, 6)
      .map((item) => ({
        id: item.id,
        title: item.title,
        meta: `${item.company} • ${item.location}`,
        link: `/jobs/${item.slug || item.id}`,
      }));

    const learningResults = learning
      .filter((item) =>
        match(item.title, item.description, item.category, item.instructor, item.difficultyLevel, item.duration, item.status)
      )
      .slice(0, 6)
      .map((item) => ({
        id: item.id,
        title: item.title,
        meta: `${item.category} • ${item.difficultyLevel}`,
        link: `/learning/${item.slug || item.id}`,
      }));

    return {
      ipos: ipoResults,
      news: newsResults,
      jobs: jobResults,
      learning: learningResults,
    };
  }, [ipos, jobs, learning, news, normalizedGlobalSearch]);

  const totalGlobalResults =
    globalResults.ipos.length +
    globalResults.news.length +
    globalResults.jobs.length +
    globalResults.learning.length;

  const onGlobalSearch = (event: FormEvent) => {
    event.preventDefault();
    setGlobalSearchTerm(globalSearchInput);
  };

  return (
    <div>
      {/* Hero */}
      <section className="ssd hero-gradient text-primary-foreground py-12 md:py-16 lg:py-24">
        <div className=" container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight mb-4">
              Your Financial <span className="text-accent">Dashboard</span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-primary-foreground/80 mb-6 md:mb-8 max-w-3xl">
              Live IPO GMP updates, market news, career opportunities, and learning resources — all in one place. Powered by dkonnects.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/ipos" className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity">
                <TrendingUp size={18} /> Explore IPOs
              </Link>
              <Link to="/news" className="inline-flex items-center justify-center gap-2 bg-primary-foreground/10 text-primary-foreground border border-primary-foreground/20 px-6 py-3 rounded-lg font-semibold text-sm hover:bg-primary-foreground/20 transition-colors">
                Latest News <ArrowRight size={16} />
              </Link>
            </div>
            <form onSubmit={onGlobalSearch} className="mt-6 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-foreground/70" />
                <input
                  type="text"
                  value={globalSearchInput}
                  onChange={(event) => setGlobalSearchInput(event.target.value)}
                  placeholder="Global search: IPO, News, Jobs, Learning, date, category..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-primary-foreground/10 border border-primary-foreground/20 text-sm text-primary-foreground placeholder:text-primary-foreground/60"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-semibold"
              >
                <Search size={15} />
                Search
              </button>
            </form>
          </motion.div>
        </div>
      </section>
      {normalizedGlobalSearch && (
        <section className="py-8 md:py-10 bg-muted/40 border-y border-border">
          <div className="container mx-auto px-4">
            <h2 className="section-title mb-2">Global Search Results</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Query: <span className="font-medium text-foreground">{globalSearchTerm}</span> • Results: {totalGlobalResults}
            </p>
            {totalGlobalResults === 0 && (
              <div className="text-sm text-muted-foreground">No matching results found across Home sections.</div>
            )}
            {totalGlobalResults > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ResultGroup title="IPOs" items={globalResults.ipos} />
                <ResultGroup title="News" items={globalResults.news} />
                <ResultGroup title="Jobs" items={globalResults.jobs} />
                <ResultGroup title="Learning" items={globalResults.learning} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* IPO Highlights */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <h2 className="section-title">IPO Highlights</h2>
            <Link to="/ipos" className="text-secondary text-sm font-medium flex items-center gap-1 hover:underline">View all <ChevronRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 items-start">
            {latestIpos.map((ipo, i) => (
              <motion.div key={ipo.id} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <Link to={`/ipos/${ipo.id}`} className="finance-card block p-5">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display font-semibold text-foreground">{ipo.company}</h3>
                    <span className={
                      ipo.status === "upcoming" ? "badge-info" :
                      ipo.status === "ongoing" ? "badge-success" :
                      ipo.status === "closing_today" ? "badge-warning" :
                      ipo.status === "allotment" ? "badge-secondary" :
                      ipo.status === "allotted" ? "badge-success" :
                      ipo.status === "listing_today" ? "badge-primary" :
                      ipo.status === "listed" ? "badge-success" :
                      ipo.status === "closed" ? "badge-destructive" :
                      "badge-warning"
                    }>{ipo.status.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div><span className="text-muted-foreground">Price Band</span><p className="font-medium text-foreground">₹{ipo.priceBand}</p></div>
                    <div><span className="text-muted-foreground">Issue Size</span><p className="font-medium text-foreground">{formatIssueSizeCr(ipo.issueSize)}</p></div>
                    <div>
                      <span className="text-muted-foreground">GMP</span>
                      <p className={`font-semibold ${ipo.gmp >= 0 ? "text-success" : "text-destructive"}`}>{formatSignedInr(ipo.gmp)} <span className="text-xs">({getGmpPercentage(ipo.gmp, ipo.priceBand)}%)</span></p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Est. Listing</span>
                      <p className="font-medium text-foreground">{formatInr(getEstimatedListingPrice(ipo.priceBand, ipo.gmp, ipo.estimatedListing))}</p>
                    </div>
                    <div><span className="text-muted-foreground">Open</span><p className="font-medium text-foreground">{formatDisplayDate(ipo.openDate)}</p></div>
                    <div><span className="text-muted-foreground">Close</span><p className="font-medium text-foreground">{formatDisplayDate(ipo.closeDate)}</p></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* News */}
      <section className="py-12 md:py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <h2 className="section-title">Latest News</h2>
            <Link to="/news" className="text-secondary text-sm font-medium flex items-center gap-1 hover:underline">All news <ChevronRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 items-start">
            {latestNews.map((n, i) => (
              <motion.div key={n.id} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <Link to={`/news/${n.slug || n.id}`} className="finance-card block p-5">
                  <span className="badge-info mb-3">{n.category}</span>
                  <h3 className="font-display font-semibold text-foreground mb-2 line-clamp-2">{n.title}</h3>
                  
                  {/* Media Section */}
                  {(n.image || n.videoUrl) && (
                    <div className="mb-3">
                      {n.image && (
                        <img 
                          src={n.image} 
                          alt={n.title}
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            console.log("News image failed to load:", n.image);
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                          onLoad={() => {
                            console.log("News image loaded successfully:", n.image);
                          }}
                        />
                      )}
                      {n.videoUrl && (
                        <YouTubeEmbed 
                          url={n.videoUrl}
                          title={n.title}
                          className="mb-3"
                        />
                      )}
                    </div>
                  )}
                 
                  <p className="text-sm text-muted-foreground mb-3">
                    {toWordPreview(sanitizeRichTextHtml(n.summary), 20)}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{n.author}</span><span>{formatDisplayDate(n.date)}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Banner */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="hero-gradient rounded-xl p-8 md:p-12 text-primary-foreground text-center">
            <Share2 size={36} className="mx-auto mb-4 text-accent" />
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Refer & Earn Rewards</h2>
            <p className="text-primary-foreground/70 max-w-lg mx-auto mb-6">Share Dashboard with your friends and earn rewards. Get your unique referral link and start earning today!</p>
            <Link to="/referral" className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity">
              Get Your Referral Link <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Jobs */}
      <section className="py-12 md:py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <h2 className="section-title">Latest Jobs</h2>
            <Link to="/jobs" className="text-secondary text-sm font-medium flex items-center gap-1 hover:underline">View all <ChevronRight size={16} /></Link>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]">
            {latestJobs.map((job, i) => (
              <motion.div key={job.id} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-4 md:mb-5 break-inside-avoid">
                <div className="finance-card p-5">
                  <div className="flex items-start justify-between mb-2">
                    <Link to={`/jobs/${job.slug || job.id}`} className="font-display font-semibold text-foreground hover:text-secondary transition-colors">
                      {job.title}
                    </Link>
                    <span className="badge-success">{job.type}</span>
                  </div>
                  {/* Media Section */}
                  {(job.imageUrl || job.videoUrl) && (
                    <div className="mb-3">
                      {job.imageUrl && (
                        <div>
                          <img 
                            src={job.imageUrl} 
                            alt={job.title}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              console.log("Job image failed to load:", job.imageUrl);
                              console.log("Error:", e);
                              e.currentTarget.src = "/placeholder.svg";
                            }}
                            onLoad={() => {
                              console.log("Job image loaded successfully:", job.imageUrl);
                            }}
                            style={{ backgroundColor: '#f3f4f6' }} // Add background to see if image area renders
                          />
                        </div>
                      )}
                      {job.videoUrl && (
                        <YouTubeEmbed 
                          url={job.videoUrl}
                          title={job.title}
                          className="mb-3"
                        />
                      )}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground mb-3">{job.company} • {job.location}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">{job.salary}</span>
                    <Link to={`/jobs/${job.id}`} className="text-secondary text-sm font-medium hover:underline">Details →</Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-4">
            <h2 className="section-title">Learning Resources</h2>
            <Link to="/learning" className="text-secondary text-sm font-medium flex items-center gap-1 hover:underline">View all <ChevronRight size={16} /></Link>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]">
            {latestLearning.map((item, i) => (
              <motion.div key={item.id} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-4 md:mb-5 break-inside-avoid">
                <div className="finance-card p-5">
                  <div className="flex items-start justify-between mb-2">
                    <Link to={`/learning/${item.slug || item.id}`} className="font-display font-semibold text-foreground hover:text-secondary transition-colors">
                      {item.title}
                    </Link>
                    <div className="flex gap-1">
                      {item.imageUrl && <ImageIcon size={16} className="text-blue-500" />}
                      {item.videoUrl && <Video size={16} className="text-red-500" />}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.difficultyLevel === 'beginner' ? 'bg-green-100 text-green-800' :
                        item.difficultyLevel === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.difficultyLevel}
                      </span>
                    </div>
                  </div>
                  {/* Media Section */}
                  {(item.imageUrl || item.videoUrl) && (
                    <div className="mb-3">
                      {item.imageUrl && (
                        <div>
                          <img 
                            src={item.imageUrl} 
                            alt={item.title}
                            className="w-full h-32 object-cover rounded-lg"
                            onError={(e) => {
                              console.log("Learning image failed to load:", item.imageUrl);
                              e.currentTarget.src = "/placeholder.svg";
                            }}
                            onLoad={() => {
                              console.log("Learning image loaded successfully:", item.imageUrl);
                            }}
                          />
                        </div>
                      )}
                      {item.videoUrl && (
                        <YouTubeEmbed 
                          url={item.videoUrl}
                          title={item.title}
                          className="mb-3"
                        />
                      )}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground mb-3">{item.instructor} • {item.duration}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">Free</span>
                    <Link to={`/learning/${item.id}`} className="text-secondary text-sm font-medium hover:underline">Details →</Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex justify-center gap-4 mb-6">
              <a 
                href="https://www.instagram.com/dkonnects_ai/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-pink-600 text-white hover:bg-pink-700 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=100077077226047&sk=about" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.youtube.com/@dkonnects" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                aria-label="YouTube"
              >
                <Youtube size={20} />
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-800 text-center">
              <p className="text-xs text-slate-400">© 2024 dkonnects. All rights reserved. | Financial Dashboard</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;

